import { useEffect, useState } from "react";
import { suggestionService } from "./service";
import { TSuggestion } from "types/suggestions";
import { Suggestion } from "./components/Suggestion";
import Pagination from "components/Pagination";
import { useSuggestionsStore } from "./store";
import { Button } from "components/Button";
import { SquareLoader } from "screens/Main/components/Loader/SquareLoader";
import { useDebounce } from "hooks/useDebounce";

export const Suggestions = () => {
  const {
    setTotalSuggestions,
    totalSuggestions,
    totalPages,
    setTotalPages,
    selectedSuggestions,
    approveProcessings,
    searchQuery,
    setSearchQuery,
    isLoading
  } = useSuggestionsStore();

  const [page, setPage] = useState(1);
  const [suggestions, setSuggestions] = useState<TSuggestion[]>([]);
  const debouncedSearchValue = useDebounce(searchQuery);

  const getSuggestions = async (
    page: number,
    take?: number,
    searchQuery?: string
  ) => {
    const data = await suggestionService.getSuggestions(
      page,
      take,
      searchQuery
    );
    setSuggestions(data.data);
    setTotalSuggestions(data.total);
    setTotalPages(data.pages);
  };

  const isSomeSelected = selectedSuggestions.length > 0;

  const approveAll = async () => {
    await suggestionService.approve(selectedSuggestions);
    getSuggestions(page);
  };

  const approveOne = async (suggestion: TSuggestion) => {
    await suggestionService.approve([suggestion.twitter]);
    getSuggestions(page);
  };

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchQuery(searchValue);
    setPage(1);
  };

  useEffect(() => {
    getSuggestions(page, 10, debouncedSearchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearchValue]);

  return (
    <div className="px-[14px] sm:px-[154px] flex flex-col items-center">
      <h1 className="mb-[50px] text-[24px] text-center px-[50px] sm:px-0 md:text-[36px] font-[700]">
        Projects Suggestions
      </h1>

      <div className="w-full mb-4 flex h-[30px]">
        <label
          htmlFor="select-all"
          className="cursor-pointer flex items-center mr-auto pl-[22px] p-2 -m-2 ml-0"
        >
          <input
            id="select-all"
            type="checkbox"
            checked={isSomeSelected}
            onChange={() =>
              suggestionService.toggleAll(suggestions.map((s) => s.twitter))
            }
            className="accent-[#FFC93D] w-4 h-4"
          />
          <span className="ml-2">
            {isSomeSelected ? "Unselect all" : "Select all"}
          </span>
        </label>

        {isSomeSelected && (
          <Button
            extraClass="h-[30px] py-0 px-2 w-fit"
            onClick={approveAll}
            disabled={approveProcessings.length > 0}
          >
            Approve selected
          </Button>
        )}
      </div>

      <input
        type="text"
        placeholder="Search by twitter"
        value={searchQuery}
        onChange={handleSearchChange}
        className="px-4 py-2 border text-black rounded-lg w-full focus:outline-none mb-4"
      />

      {isLoading ? (
        <div className="flex w-full  my-[20px] justify-center  items-center">
          <SquareLoader size={32} />
        </div>
      ) : (
        <table className="w-full table-auto bg-[#171717]">
          <thead className="bg-[#202020]">
            <tr>
              <th className="w-1 text-center p-2">Select</th>
              <th className="w-1 text-center p-2">Count</th>
              <th className="w-1 text-start p-2">Twitter</th>
              <th className="flex-1 text-center p-2">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-[#202020]">
            {suggestions.map((suggestion) => (
              <Suggestion
                isSelected={selectedSuggestions.includes(suggestion.twitter)}
                onSelect={() =>
                  suggestionService.toggleSuggestion(suggestion.twitter)
                }
                suggestion={suggestion}
                key={suggestion.twitter}
                onApprove={() => approveOne(suggestion)}
                isOnApprove={approveProcessings.includes(suggestion.twitter)}
              />
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-6">
        <Pagination
          currentPage={page}
          totalItems={totalSuggestions}
          totalPages={totalPages}
          onPageChange={setPage}
          itemsPerPage={10}
          onItemsPerPageChange={() => {}}
        />
      </div>
    </div>
  );
};
